import { useState } from "react";
import SEO from "../../components/base/SEO";
import Footer from "../../components/feature/Footer";
import Header from "../../components/feature/Header";

const deliveryFlow = [
  {
    title: "接收訂單",
    description: "Excel、API、客戶入口或人手落單，先集中成可排程任務。",
    icon: "ri-file-list-3-line",
    label: "Order",
  },
  {
    title: "排線派車",
    description: "按區域、時段、載重和優先次序安排車線，減少臨場改動。",
    icon: "ri-route-line",
    label: "Route",
  },
  {
    title: "司機執行",
    description: "司機 App 接收任務、導航、更新狀態、上傳相片和簽收。",
    icon: "ri-smartphone-line",
    label: "Driver",
  },
  {
    title: "即時追蹤",
    description: "後勤知道車在哪、單到哪，客戶查詢不用逐個 WhatsApp 問。",
    icon: "ri-map-pin-line",
    label: "Track",
  },
  {
    title: "POD 對數",
    description: "送貨證明、異常、成本和完成率回到報表，方便追蹤和對帳。",
    icon: "ri-file-chart-line",
    label: "POD",
  },
] as const;

const modules = [
  {
    title: "智能排線",
    description: "按送貨點、車輛、司機和時段建立車線，先預覽再派單。",
    image: "/tms-assign.png",
    icon: "ri-route-line",
  },
  {
    title: "任務管理",
    description: "每日送貨任務集中管理，支援批量匯入、調整排序和狀態同步。",
    image: "/tms-order.png",
    icon: "ri-list-check-3",
  },
  {
    title: "即時追蹤",
    description: "地圖查看車輛位置和送貨進度，後勤可更快處理客戶查詢。",
    image: "/tms-realtime.png",
    icon: "ri-map-pin-time-line",
  },
  {
    title: "車隊資料",
    description: "管理車牌、車型、載重、維修、支出和日常營運記錄。",
    image: "/tms-vehicle.png",
    icon: "ri-truck-line",
  },
  {
    title: "司機 App",
    description: "司機用手機接任務、導航、拍照、簽收和回報異常。",
    image: "/tms-schedule.png",
    icon: "ri-user-location-line",
  },
  {
    title: "報表分析",
    description: "掌握準時率、完成率、車輛成本、取消原因和司機表現。",
    image: "/tms-payroll.png",
    icon: "ri-bar-chart-box-line",
  },
] as const;

const differencePoints = [
  {
    title: "不是只看車在哪",
    description: "追蹤只是其中一步，真正要管理的是由落單、派車、簽收到對帳的整條流程。",
    icon: "ri-flow-chart",
  },
  {
    title: "不靠 WhatsApp 做調度中心",
    description: "司機狀態、POD、異常和客戶查詢需要回到系統，不應散落在群組訊息。",
    icon: "ri-whatsapp-line",
  },
  {
    title: "前線容易用，後勤才有數據",
    description: "司機 App 要簡單，後勤才可以收到準確狀態，管理層才有可信報表。",
    icon: "ri-user-smile-line",
  },
  {
    title: "可先由少量車輛開始",
    description: "不用一次重建整個車隊流程，可以先由最難排線或最多查詢的一組車開始。",
    icon: "ri-seedling-line",
  },
] as const;

const plans = [
  {
    name: "1 部車起步",
    price: "HK$300",
    description: "適合小型送貨團隊先把任務、司機和 POD 管起來。",
    features: ["送貨任務管理", "司機管理", "即時追蹤", "POD 簽收記錄"],
    highlighted: false,
  },
  {
    name: "3 部車團隊",
    price: "HK$1,200",
    description: "加入排線和團隊管理，適合開始有固定車隊分工的公司。",
    features: ["全部起步功能", "智能排線", "排線預覽", "進階報表"],
    highlighted: true,
  },
  {
    name: "5 部車或以上",
    price: "HK$2,000+",
    description: "適合需要多司機、多車線和更完整營運報表的團隊。",
    features: ["全部團隊功能", "更多帳號支援", "跨團隊協作", "上線設定協助"],
    highlighted: false,
  },
  {
    name: "客製化方案",
    price: "聯絡報價",
    description: "適合需要 API、客戶入口、特殊計費或內部流程串接的企業。",
    features: ["API 串接", "客製化報表", "專屬支援", "SLA 服務安排"],
    highlighted: false,
  },
] as const;

const industries = [
  "電商物流送貨",
  "第三方物流",
  "B2B 企業配送",
  "生鮮凍肉配送",
  "快遞包裹派送",
  "傢俬電器送貨",
  "醫藥物流配送",
  "建材五金配送",
] as const;

const faqs = [
  {
    question: "TMS 是否需要安裝軟件？",
    answer:
      "後勤和管理層使用 Web 版，開瀏覽器即可使用；司機使用手機 App 接任務、導航、拍照和簽收。",
  },
  {
    question: "可以先由少量車輛開始嗎？",
    answer:
      "可以。建議先由最常延誤、最多查詢或最難排線的一組車開始，流程穩定後再擴展。",
  },
  {
    question: "可否匯入現有 Excel 訂單？",
    answer:
      "可以。系統支援批量匯入送貨任務、客戶資料和送貨點，亦可按需要規劃 API 串接。",
  },
  {
    question: "POD 可以保存甚麼資料？",
    answer:
      "可保存相片、簽名、時間、位置和送貨狀態，方便客戶查詢、異常跟進和月底對帳。",
  },
  {
    question: "適合不是大型車隊的公司嗎？",
    answer:
      "適合。TMS 的重點不是車多才用，而是當排線、追蹤、POD 和客戶查詢開始靠人手補救，就值得導入。",
  },
] as const;

export default function TMS() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const openWhatsApp = (message: string) => {
    window.open(`https://wa.me/85294252903?text=${encodeURIComponent(message)}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="AutoTMS 車隊管理及送貨系統 | 香港 TMS 運輸管理系統"
        description="AutoTMS 是香港車隊管理及送貨系統，支援排線派車、司機 App、即時追蹤、POD 簽收、車隊資料及營運報表，協助送貨團隊減少 WhatsApp 調度和人手追單。"
        keywords="TMS, 車隊管理系統, 送貨系統, 送貨App, 跟車App, Van仔App, 運輸系統, AI排線, 車隊調度, POD簽收, 香港TMS"
        ogImage="https://autochain.com.hk/tms-og-image.png"
        canonicalUrl="/tms"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "AutoTMS 車隊管理系統",
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web Browser, iOS, Android",
            description:
              "AutoTMS 專為香港送貨公司打造，支援排線派車、即時追蹤、司機 App、POD 簽收和營運報表。",
            url: "https://autochain.com.hk/tms",
            offers: {
              "@type": "AggregateOffer",
              lowPrice: "300",
              highPrice: "2000",
              priceCurrency: "HKD",
              offerCount: "4",
            },
            provider: {
              "@type": "Organization",
              name: "AUTOCHAIN TECHNOLOGY",
            },
            featureList: [
              "排線派車",
              "即時追蹤",
              "司機 App",
              "車隊管理",
              "POD 簽收記錄",
              "數據報表",
            ],
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首頁",
                item: "https://autochain.com.hk/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "TMS 運輸管理系統",
                item: "https://autochain.com.hk/tms",
              },
            ],
          }),
        }}
      />

      <Header />

      <main>
        <section
          className="relative overflow-hidden bg-cover bg-center text-white"
          style={{
            backgroundImage:
              "linear-gradient(90deg, rgba(5,10,18,0.92) 0%, rgba(5,10,18,0.78) 46%, rgba(5,10,18,0.46) 100%), url('/tms-main.jpg')",
          }}
        >
          <div className="mx-auto grid min-h-[82vh] max-w-7xl grid-cols-1 gap-12 px-4 py-24 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:px-8">
            <div>
              <div className="inline-flex items-center gap-2 border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium text-cyan-100 backdrop-blur">
                <i className="ri-truck-line text-cyan-300"></i>
                TMS 車隊管理及送貨系統
              </div>
              <h1 className="mt-6 text-4xl font-bold leading-tight md:text-6xl">
                由落單、排線到 POD，車隊營運不要再靠 WhatsApp 追
              </h1>
              <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-100 md:text-xl">
                AutoTMS 幫香港送貨團隊把每日訂單、車線、司機狀態、即時追蹤和簽收記錄接成一條清晰流程。
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href="https://autotms-portal.autochain.com.hk/signup"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-cyan-400 px-6 py-3 font-semibold text-slate-950 transition-colors hover:bg-cyan-300"
                >
                  免費試用
                  <i className="ri-arrow-right-line"></i>
                </a>
                <button
                  onClick={() =>
                    openWhatsApp("您好！我想了解 AutoTMS 車隊管理及送貨系統，希望預約產品示範。")
                  }
                  className="inline-flex items-center justify-center gap-2 border border-white/40 bg-white/10 px-6 py-3 font-semibold text-white transition-colors hover:bg-white hover:text-slate-950"
                >
                  <i className="ri-whatsapp-line"></i>
                  預約示範
                </button>
              </div>
              <p className="mt-5 text-sm text-slate-300">
                適合送貨公司、3PL、電商履行、B2B 配送及本地車隊。
              </p>
            </div>

            <div className="border border-white/10 bg-slate-950/80 p-5 shadow-2xl backdrop-blur md:p-7">
              <div className="flex items-center justify-between border-b border-white/10 pb-5">
                <div>
                  <div className="text-sm font-semibold uppercase text-cyan-300">
                    Dispatch Board
                  </div>
                  <div className="mt-1 text-2xl font-bold">今日車隊狀態</div>
                </div>
                <div className="border border-cyan-300/40 bg-cyan-300/10 px-3 py-2 text-sm font-semibold text-cyan-100">
                  Live
                </div>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-3">
                {[
                  ["待派車", "18"],
                  ["運送中", "42"],
                  ["已簽收", "126"],
                ].map(([label, value]) => (
                  <div key={label} className="border border-white/10 bg-white/5 p-4">
                    <div className="text-sm text-slate-400">{label}</div>
                    <div className="mt-2 text-3xl font-bold text-white">{value}</div>
                  </div>
                ))}
              </div>

              <div className="mt-5 space-y-3">
                {deliveryFlow.map((item, index) => (
                  <div
                    key={item.title}
                    className="grid grid-cols-[auto_1fr_auto] items-center gap-4 border border-white/10 bg-white/[0.06] p-4"
                  >
                    <div className="flex h-11 w-11 items-center justify-center bg-cyan-400 text-xl text-slate-950">
                      <i className={item.icon}></i>
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                        0{index + 1} / {item.label}
                      </div>
                      <div className="mt-1 font-semibold text-white">{item.title}</div>
                    </div>
                    <i className="ri-arrow-right-line text-cyan-300"></i>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-slate-50 py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
              <div>
                <span className="text-sm font-semibold uppercase text-teal-700">
                  營運瓶頸
                </span>
                <h2 className="mt-3 text-3xl font-bold leading-tight text-slate-950 md:text-4xl">
                  車隊管理問題通常不是「看不到車」，而是流程斷開
                </h2>
                <p className="mt-5 text-lg leading-8 text-slate-600">
                  如果排線、司機狀態、POD 和客戶查詢散落在不同 WhatsApp 對話，後勤會一直追，管理層也很難知道真正成本。
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {differencePoints.map((point) => (
                  <div key={point.title} className="border border-slate-200 bg-white p-6">
                    <div className="flex h-11 w-11 items-center justify-center bg-slate-950 text-xl text-white">
                      <i className={point.icon}></i>
                    </div>
                    <h3 className="mt-5 text-xl font-semibold text-slate-950">
                      {point.title}
                    </h3>
                    <p className="mt-3 text-sm leading-6 text-slate-600">
                      {point.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="text-sm font-semibold uppercase text-orange-700">
                運作流程
              </span>
              <h2 className="mt-3 text-3xl font-bold leading-tight text-slate-950 md:text-4xl">
                由接單到簽收，讓每一步都有狀態、有紀錄、有責任人
              </h2>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-4 lg:grid-cols-5">
              {deliveryFlow.map((item, index) => (
                <div key={item.title} className="relative border border-slate-200 bg-white p-5">
                  {index < deliveryFlow.length - 1 && (
                    <div className="absolute -right-3 top-1/2 z-10 hidden h-6 w-6 -translate-y-1/2 items-center justify-center bg-white text-slate-400 lg:flex">
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  )}
                  <div className="flex h-11 w-11 items-center justify-center bg-cyan-400 text-xl text-slate-950">
                    <i className={item.icon}></i>
                  </div>
                  <div className="mt-5 text-sm font-semibold text-teal-700">
                    0{index + 1} / {item.label}
                  </div>
                  <h3 className="mt-2 text-xl font-semibold text-slate-950">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-slate-950 py-16 text-white md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
              <div className="max-w-3xl">
                <span className="text-sm font-semibold uppercase text-cyan-300">
                  功能模組
                </span>
                <h2 className="mt-3 text-3xl font-bold md:text-4xl">
                  日常調度需要的核心工具，集中在同一套 TMS
                </h2>
              </div>
              <p className="max-w-2xl text-lg leading-8 text-slate-300">
                功能不是越多越好，重點是每個模組都接住每日送貨現場。
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
              {modules.map((module) => (
                <div key={module.title} className="group overflow-hidden border border-white/10 bg-white/5">
                  <div className="aspect-[16/10] overflow-hidden bg-slate-800">
                    <img
                      src={module.image}
                      alt={module.title}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex h-10 w-10 items-center justify-center bg-cyan-400 text-xl text-slate-950">
                      <i className={module.icon}></i>
                    </div>
                    <h3 className="mt-5 text-xl font-semibold">{module.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-slate-300">
                      {module.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_0.8fr] lg:items-center lg:px-8">
            <div>
              <span className="text-sm font-semibold uppercase text-teal-700">
                司機 App
              </span>
              <h2 className="mt-3 text-3xl font-bold leading-tight text-slate-950 md:text-4xl">
                司機不用打開後台，只需要完成今天的路線
              </h2>
              <p className="mt-5 text-lg leading-8 text-slate-600">
                App 只保留前線需要的動作：查看任務、導航、更新狀態、拍照簽收、回報異常。後勤就能即時收到進度。
              </p>
              <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
                {["接收任務", "一鍵導航", "拍照簽收", "異常回報"].map((item) => (
                  <div key={item} className="border border-slate-200 p-5">
                    <i className="ri-check-line text-xl text-teal-600"></i>
                    <div className="mt-3 font-semibold text-slate-950">{item}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="border border-slate-200 bg-slate-950 p-5 text-white">
              <div className="mx-auto max-w-[280px] border-[10px] border-slate-800 bg-slate-900 p-4 shadow-2xl">
                <div className="border-b border-white/10 pb-4">
                  <div className="text-sm text-cyan-300">AutoTMS Driver</div>
                  <div className="mt-1 text-2xl font-bold">今日任務</div>
                </div>
                <div className="mt-4 space-y-3">
                  {[
                    ["09:30", "觀塘 A 座", "已送達"],
                    ["10:45", "九龍灣倉", "運送中"],
                    ["12:10", "荃灣客戶", "待出發"],
                  ].map(([time, place, status]) => (
                    <div key={place} className="border border-white/10 bg-white/5 p-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-semibold text-white">{time}</span>
                        <span className="text-cyan-300">{status}</span>
                      </div>
                      <div className="mt-2 text-sm text-slate-300">{place}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 bg-cyan-400 px-4 py-3 text-center font-semibold text-slate-950">
                  開始導航
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-slate-50 py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="text-sm font-semibold uppercase text-orange-700">
                適用場景
              </span>
              <h2 className="mt-3 text-3xl font-bold text-slate-950 md:text-4xl">
                只要每日要排車、追車、交 POD，就適合導入
              </h2>
            </div>
            <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4">
              {industries.map((industry) => (
                <div key={industry} className="border border-slate-200 bg-white p-5 font-semibold text-slate-800">
                  {industry}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
              <div className="max-w-3xl">
                <span className="text-sm font-semibold uppercase text-teal-700">
                  方案
                </span>
                <h2 className="mt-3 text-3xl font-bold text-slate-950 md:text-4xl">
                  可由少量車輛開始，再按營運規模擴展
                </h2>
              </div>
              <button
                onClick={() =>
                  openWhatsApp("您好！我想了解 AutoTMS 不同車隊規模的方案和報價。")
                }
                className="inline-flex items-center justify-center gap-2 bg-slate-950 px-6 py-3 font-semibold text-white transition-colors hover:bg-slate-800"
              >
                WhatsApp 查詢
                <i className="ri-whatsapp-line"></i>
              </button>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
              {plans.map((plan) => (
                <div
                  key={plan.name}
                  className={`border p-6 ${
                    plan.highlighted
                      ? "border-cyan-400 bg-cyan-50"
                      : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="text-xl font-bold text-slate-950">{plan.name}</div>
                  <div className="mt-4 text-3xl font-bold text-slate-950">
                    {plan.price}
                    {plan.price.startsWith("HK$") && (
                      <span className="text-base font-medium text-slate-500"> /月</span>
                    )}
                  </div>
                  <p className="mt-4 text-sm leading-6 text-slate-600">
                    {plan.description}
                  </p>
                  <div className="mt-6 space-y-3">
                    {plan.features.map((feature) => (
                      <div key={feature} className="flex items-start gap-2 text-sm text-slate-700">
                        <i className="ri-check-line mt-0.5 text-teal-600"></i>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-slate-50 py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="text-sm font-semibold uppercase text-teal-700">
                FAQ
              </span>
              <h2 className="mt-3 text-3xl font-bold text-slate-950 md:text-4xl">
                常見問題
              </h2>
            </div>

            <div className="mt-10 space-y-3">
              {faqs.map((faq, index) => (
                <div key={faq.question} className="border border-slate-200 bg-white">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left font-semibold text-slate-950"
                  >
                    {faq.question}
                    <i
                      className={`ri-arrow-down-s-line text-2xl text-slate-400 transition-transform ${
                        openFaq === index ? "rotate-180" : ""
                      }`}
                    ></i>
                  </button>
                  {openFaq === index && (
                    <div className="border-t border-slate-200 px-5 py-4 text-sm leading-6 text-slate-600">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-gradient-to-r from-teal-600 to-cyan-600 py-16 text-white md:py-20">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold md:text-4xl">
              先把最混亂的一條車線整理清楚
            </h2>
            <p className="mt-5 text-lg leading-8 text-white/90">
              不需要一開始導入所有功能。我們可以先看你的車隊規模、排線方式和 POD 流程，再建議最適合的起步方案。
            </p>
            <button
              onClick={() =>
                openWhatsApp("您好！我想了解 AutoTMS，想先討論我們車隊的排線和送貨流程。")
              }
              className="mt-8 inline-flex items-center justify-center gap-2 bg-white px-7 py-3 font-semibold text-teal-700 transition-colors hover:bg-slate-100"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              WhatsApp 查詢
            </button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
